import { WebPubSubClient } from "@azure/web-pubsub-client";
import "./styles.css";
import { getIdentity, getPlayerState, getStats, landCatch, negotiate, reelCatch, startCast, type Activity, type Profile, type ScoreRow } from "./api";
import { astrakhanNow, castPhase, evaluateCatchDecision, localCatch, sanitizeChat, seasonFor, weatherFor, type CatchDecision, type CatchResult, type DecisionOutcome, type RoomKind, type Tackle } from "./game";
import { fishName, initialLocale, isDefaultGuestName, legacyCatchDetails, seasonName, translate, weatherName, type Locale } from "./i18n";

const app = document.querySelector<HTMLDivElement>("#app")!;
let locale: Locale = initialLocale(localStorage.getItem("river-language"), navigator.language);
const storedName = localStorage.getItem("river-name");
let usingDefaultName = !storedName || isDefaultGuestName(storedName);
const savedName = usingDefaultName ? translate(locale, "guestName") : storedName!;
if (storedName && usingDefaultName) localStorage.removeItem("river-name");
const savedId = localStorage.getItem("river-id") || crypto.randomUUID();
localStorage.setItem("river-id", savedId);

let profile: Profile = { id: savedId, name: savedName };
let lobbyName = savedName;
let room: RoomKind = "public";
let accessCode = "";
let tackle: Tackle = "float";
let pending: CatchResult | null = null;
let landed: CatchResult | null = null;
let landedTackle: Tackle = "float";
let biteAt = 0;
let reelDeadline = 0;
let score = 0;
let totalKg = 0;
let catches = 0;
let keptKg = 0;
let recipeUnlocked = false;
let guardianUnlocked = false;
let seasonsVisited = new Set<string>();
let connected = false;
let pubsub: WebPubSubClient | null = null;
let gameSession = 0;
let leaderboard: ScoreRow[] = [];
let activity: Activity = { casts: 0, catches: 0, totalKg: 0, online: 1 };
const peers = new Map<string, string>();
type ChatItem =
  | { kind: "message"; name: string; text: string; mine?: boolean }
  | { kind: "catch"; playerName: string; fishId: string; fallbackFish: string; weightKg: number | null; sighting: boolean };
const chat: ChatItem[] = [];
let chatDraft = "";

function esc(value: string) {
  return value.replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]!);
}

const t = (key: Parameters<typeof translate>[1], values?: Record<string, string | number>) => translate(locale, key, values);
const localNumber = (value: number) => value.toLocaleString(locale === "ru" ? "ru-RU" : "en-US");

function progressKey(item: string) { return `river-${item}:${room}:${profile.id}`; }
function loadProgress() {
  keptKg = Number(localStorage.getItem(progressKey("kept-kg")) || 0);
  recipeUnlocked = localStorage.getItem(progressKey("recipe")) === "yes";
  guardianUnlocked = localStorage.getItem(progressKey("guardian")) === "yes";
  try { seasonsVisited = new Set<string>(JSON.parse(localStorage.getItem(progressKey("seasons")) || "[]") as string[]); }
  catch { seasonsVisited = new Set<string>(); }
  seasonsVisited.add(seasonFor());
  localStorage.setItem(progressKey("seasons"), JSON.stringify([...seasonsVisited]));
}

function languageButton() {
  return `<button class="language" type="button" aria-label="${esc(t("switchLanguage"))}" title="${esc(t("switchLanguage"))}">${t("languageButton")}</button>`;
}

function applyLocale(render: () => void) {
  const previousName = profile.name;
  locale = locale === "en" ? "ru" : "en";
  if (usingDefaultName) {
    profile.name = t("guestName");
    lobbyName = profile.name;
  }
  localStorage.setItem("river-language", locale);
  if (connected && profile.name !== previousName) sendEvent("presence", { action: "update" });
  render();
}

function captureLobbyDraft() {
  const name = document.querySelector<HTMLInputElement>("#name");
  const access = document.querySelector<HTMLInputElement>("#access");
  if (name) {
    lobbyName = name.value;
    usingDefaultName = lobbyName === translate(locale, "guestName");
  }
  if (access) accessCode = access.value;
}

function updateDocumentLanguage() {
  document.documentElement.lang = locale;
  document.title = t("pageTitle");
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", t("pageDescription"));
}

function levelDetails() {
  const level = guardianUnlocked && seasonsVisited.size >= 4 ? 3 : seasonsVisited.size >= 4 ? 2 : 1;
  return { level, name: t(level === 1 ? "levelOne" : level === 2 ? "levelTwo" : "levelThree") };
}

function reasonText(reason: DecisionOutcome["reason"]) {
  return t(reason === "protected" ? "reasonProtected" : reason === "undersized" ? "reasonUndersized" : reason === "limit" ? "reasonLimit" : "reasonCommercial");
}

function renderLobby(error = "") {
  updateDocumentLanguage();
  const weather = weatherFor();
  const season = seasonFor().toLowerCase();
  app.innerHTML = `
    <main class="lobby season-${season}">
      <div class="lobby-shade"></div>
      <nav class="topbar"><a href="https://doctorlukina.com" class="brand" aria-label="${esc(t("backToDrLukina"))}">DR. LUKINA</a>${languageButton()}</nav>
      <section class="intro" aria-labelledby="game-title">
        <p class="eyebrow">${t("eyebrow")}</p>
        <h1 id="game-title">${t("titleFirst")}<br><em>${t("titleSecond")}</em></h1>
        <p class="lede">${t("lede")}</p>
        <div class="live-facts"><span><i class="pulse"></i>${t("seasonNow", { season: seasonName(locale, seasonFor()) })}</span><span>${weather.temp}°C · ${weatherName(locale, weather.label)}</span></div>
      </section>
      <section class="entry" aria-label="${esc(t("enterGame"))}">
        <div class="room-switch" role="tablist" aria-label="${esc(t("roomType"))}">
          <button class="${room === "public" ? "active" : ""}" data-room="public" role="tab">${t("publicRiver")}</button>
          <button class="${room === "family" ? "active" : ""}" data-room="family" role="tab">${t("lukinFamily")}</button>
        </div>
        <label>${t("anglerName")}<input id="name" maxlength="24" autocomplete="nickname" value="${esc(lobbyName)}"></label>
        ${room === "family" ? `<label>${t("familyCode")}<input id="access" type="password" autocomplete="current-password" placeholder="${esc(t("familyCodePlaceholder"))}" value="${esc(accessCode)}"></label>` : ""}
        ${error ? `<p class="error" role="alert">${esc(error)}</p>` : ""}
        <button id="enter" class="primary">${t("enterRiver")} <span>→</span></button>
        <a class="auth-link" href="/.auth/login/aad?post_login_redirect_uri=/">${t("signIn")}</a>
        <p class="entry-note">${t("privacyNote")}</p>
      </section>
      <footer class="lobby-footer">${t("footer")}</footer>
    </main>`;
  document.querySelector<HTMLButtonElement>(".language")!.onclick = () => { captureLobbyDraft(); applyLocale(() => renderLobby()); };
  document.querySelectorAll<HTMLButtonElement>("[data-room]").forEach((button) => button.onclick = () => { captureLobbyDraft(); room = button.dataset.room as RoomKind; renderLobby(); });
  document.querySelector<HTMLButtonElement>("#enter")!.onclick = enterRoom;
}

async function enterRoom() {
  const input = document.querySelector<HTMLInputElement>("#name")!;
  const name = input.value.trim().replace(/\s+/g, " ");
  if (name.length < 2) return renderLobby(t("shortNameError"));
  accessCode = document.querySelector<HTMLInputElement>("#access")?.value || "";
  lobbyName = name;
  profile.name = name;
  usingDefaultName = isDefaultGuestName(name);
  gameSession++;
  if (usingDefaultName) localStorage.removeItem("river-name"); else localStorage.setItem("river-name", name);
  try {
    const identity = await getIdentity();
    const activeName = usingDefaultName ? t("guestName") : lobbyName.trim().replace(/\s+/g, " ");
    lobbyName = activeName;
    profile = identity ? { ...identity, name: activeName } : { ...profile, name: activeName };
    loadProgress();
    try {
      keptKg = (await getPlayerState(room, profile, accessCode)).keptKg;
      localStorage.setItem(progressKey("kept-kg"), String(keptKg));
    } catch { /* offline practice keeps scoped local progress */ }
    const token = await negotiate(room, profile, accessCode);
    pubsub = new WebPubSubClient(token.url);
    pubsub.on("connected", () => { connected = true; sendEvent("presence", { action: "join" }); renderGame(); });
    pubsub.on("disconnected", () => { connected = false; renderGame(); });
    pubsub.on("group-message", (event) => receiveEvent(event.message.data));
    await pubsub.start();
  } catch {
    if (room === "family") return renderLobby(t("familyRoomError"));
    connected = false;
  }
  if (await refreshStats()) renderGame();
}

function sendEvent(type: string, payload: object) {
  if (!pubsub || !connected) return;
  pubsub.sendToGroup(`room-${room}`, { type, profile, ...payload }, "json", { noEcho: true }).catch(() => undefined);
}

function receiveEvent(raw: unknown) {
  const event = typeof raw === "string" ? JSON.parse(raw) : raw as Record<string, unknown>;
  const sender = event.profile as Profile | undefined;
  if (!sender || sender.id === profile.id) return;
  if (event.type === "presence") {
    if (event.action === "leave") peers.delete(sender.id); else peers.set(sender.id, sender.name);
  }
  if (event.type === "chat" && typeof event.text === "string") chat.push({ kind: "message", name: sender.name, text: event.text.slice(0, 180) });
  if (event.type === "catch") {
    const eventId = typeof event.fishId === "string" ? event.fishId : "";
    const fallback = typeof event.fish === "string" ? event.fish : eventId;
    const legacy = eventId ? null : legacyCatchDetails(fallback);
    const id = eventId || legacy?.fishId || "";
    const weight = typeof event.weightKg === "number" ? event.weightKg : legacy?.weightKg ?? null;
    const sighting = event.protected === true || event.sighting === true || legacy?.sighting === true || (weight === null && /sighting/i.test(fallback));
    if (id || fallback) chat.push({ kind: "catch", playerName: sender.name, fishId: id, fallbackFish: fallback, weightKg: weight, sighting });
  }
  renderGame();
}

async function refreshStats(session = gameSession, activeRoom = room) {
  try {
    const stats = await getStats(activeRoom);
    if (session !== gameSession || activeRoom !== room) return false;
    ({ leaderboard, activity } = stats);
  } catch { /* local development fallback */ }
  return session === gameSession && activeRoom === room;
}

function renderChatItem(item: ChatItem) {
  if (item.kind === "message") return `<p class="${item.mine ? "mine" : ""}"><b>${esc(item.name)}</b>${esc(item.text)}</p>`;
  const translatedFish = fishName(locale, item.fishId, item.fallbackFish);
  const text = item.sighting
    ? t("playerSighted", { name: item.playerName, fish: translatedFish })
    : item.weightKg === null
    ? t("playerLanded", { name: item.playerName, fish: translatedFish })
    : t("playerLandedWeight", { name: item.playerName, weight: item.weightKg, fish: translatedFish });
  return `<p><b>${esc(t("river"))}</b>${esc(text)}</p>`;
}

function renderGame(message?: string) {
  updateDocumentLanguage();
  const phase = castPhase(Boolean(pending), biteAt, reelDeadline);
  const status = message ?? (phase === "bite" ? t("bite") : phase === "waiting" ? t("watchLine") : t("chooseTackle"));
  const now = astrakhanNow();
  const weather = weatherFor();
  const season = seasonFor().toLowerCase();
  const level = levelDetails();
  const peerList = [...peers.entries()].slice(0, 5);
  app.innerHTML = `
    <main class="game season-${season} ${pending ? "waiting" : ""}">
      <div class="water-ripples"></div>
      <header class="gamebar"><button id="leave" class="icon-button" aria-label="${esc(t("leaveRoom"))}">←</button><div><strong>${room === "family" ? t("familyBend") : t("publicRiverbank")}</strong><small><i class="${connected ? "pulse" : "offline"}"></i>${connected ? t("liveMultiplayer") : t("practiceConnection")}</small></div>${languageButton()}<div class="clock"><strong>${now.toLocaleTimeString(locale === "ru" ? "ru-RU" : "en-US", { hour: "2-digit", minute: "2-digit" })}</strong><small>${t("astrakhan")} · ${seasonName(locale, seasonFor())}</small></div></header>
      <section class="conditions" aria-label="${esc(t("riverConditions"))}"><span>${weather.temp}°</span><div><strong>${weatherName(locale, weather.label)}</strong><small>${t("currentConditions")}</small></div></section>
      <section class="anglers" aria-label="${esc(t("nearbyAnglers"))}">${peerList.map(([, name], i) => `<div class="angler" style="--x:${18 + i * 15}%" title="${esc(name)}"><span>${esc(name.slice(0, 1).toUpperCase())}</span><i></i></div>`).join("")}<p>${t("fishingThisBend", { count: Math.max(1, peers.size + 1) })}</p></section>
      <div class="float ${pending ? "cast" : ""}" aria-hidden="true"><i></i></div>
      <section class="score-card"><small>${t("levelLabel", { level: level.level })} · ${esc(level.name)}</small><strong>${localNumber(score)}</strong><span>${t("fishAndWeight", { count: catches, weight: totalKg.toFixed(2) })}</span><span>${t("keptAndLimit", { weight: keptKg.toFixed(2) })}</span><span>${t("seasonJournal", { count: seasonsVisited.size })}</span></section>
      <section class="play-card">
        <p id="status" aria-live="polite">${esc(status)}</p>
        ${landed ? `<div class="catch-decision"><strong>${esc(t("decideCatch", { weight: landed.weightKg, fish: fishName(locale, landed.species.id, landed.species.name), minimum: landed.species.protected ? "—" : landed.species.commercialMinKg }))}</strong><div><button data-decision="keep">${t("keepFish")}</button><button data-decision="release">${t("releaseFish")}</button></div><small>${t("sizeRuleNote")}</small></div>` : `<div class="tackle" role="radiogroup" aria-label="${esc(t("tackle"))}">
          <button data-tackle="float" class="${tackle === "float" ? "selected" : ""}" ${pending ? "disabled" : ""}><span>⌁</span>${t("floatRod")}<small>${t("floatHint")}</small></button>
          <button data-tackle="donka" class="${tackle === "donka" ? "selected" : ""}" ${pending ? "disabled" : ""}><span>⌇</span>${t("donka")}<small>${t("donkaHint")}</small></button>
          <button data-tackle="lure" class="${tackle === "lure" ? "selected" : ""}" ${pending ? "disabled" : ""}><span>↝</span>${t("lure")}<small>${t("lureHint")}</small></button>
        </div><button id="cast" class="cast-button ${pending ? "reel-state" : ""} ${phase === "bite" ? "bite" : ""}">${phase === "bite" ? t("reelNow") : pending ? t("waitForBite") : t("castLine")}</button>`}
      </section>
      <aside class="social-panel">
        <details open><summary>${t("riverChat")} <span>${chat.length}</span></summary><div class="messages">${chat.slice(-6).map(renderChatItem).join("") || `<p class=empty>${t("riverQuiet")}</p>`}</div><form id="chat-form"><input id="chat-input" maxlength="180" value="${esc(chatDraft)}" placeholder="${esc(t("chatPlaceholder"))}" aria-label="${esc(t("chatMessage"))}"><button aria-label="${esc(t("send"))}">↑</button></form></details>
        <details><summary>${t("leaderboard")}</summary><ol class="leaderboard">${leaderboard.slice(0, 5).map((row) => `<li><span>${esc(row.name)}</span><b>${row.score}</b></li>`).join("") || `<li><span>${t("firstCatch")}</span></li>`}</ol></details>
        <details><summary>${t("todaysActivity")}</summary><div class="stats"><span><b>${activity.casts}</b> ${t("casts")}</span><span><b>${activity.catches}</b> ${t("catches")}</span><span><b>${activity.totalKg.toFixed(1)}</b> ${t("released")}</span></div></details>
        ${recipeUnlocked ? `<details class="recipe"><summary>${t("recipeUnlocked")}</summary><div><strong>${t("recipeTitle")}</strong><p>${t("recipeBody")}</p></div></details>` : ""}
      </aside>
    </main>`;
  document.querySelector<HTMLButtonElement>("#leave")!.onclick = () => { gameSession++; sendEvent("presence", { action: "leave" }); pubsub?.stop(); pending = null; landed = null; renderLobby(); };
  document.querySelector<HTMLButtonElement>(".language")!.onclick = () => {
    chatDraft = document.querySelector<HTMLInputElement>("#chat-input")?.value || "";
    applyLocale(() => renderGame());
  };
  document.querySelectorAll<HTMLButtonElement>("[data-tackle]").forEach((button) => button.onclick = () => { tackle = button.dataset.tackle as Tackle; renderGame(); });
  const castButton = document.querySelector<HTMLButtonElement>("#cast");
  if (castButton) castButton.onclick = pending ? tryReel : castLine;
  document.querySelectorAll<HTMLButtonElement>("[data-decision]").forEach((button) => button.onclick = () => decideCatch(button.dataset.decision as CatchDecision));
  document.querySelector<HTMLInputElement>("#chat-input")!.oninput = (event) => { chatDraft = (event.currentTarget as HTMLInputElement).value; };
  document.querySelector<HTMLFormElement>("#chat-form")!.onsubmit = (event) => { event.preventDefault(); const text = sanitizeChat(chatDraft); if (!text) return; chat.push({ kind: "message", name: profile.name, text, mine: true }); sendEvent("chat", { text }); chatDraft = ""; renderGame(); };
}

async function castLine() {
  if (pending) return;
  try { pending = await startCast(room, profile, tackle, accessCode); }
  catch { pending = localCatch(tackle); }
  biteAt = Date.now() + pending.biteDelayMs;
  reelDeadline = biteAt + pending.windowMs;
  activity.casts++;
  renderGame();
  window.setTimeout(() => {
    if (!pending) return;
    renderGame();
    navigator.vibrate?.([80, 40, 120]);
  }, pending.biteDelayMs);
  window.setTimeout(() => { if (pending && Date.now() > reelDeadline) { pending = null; renderGame(t("slippedAway")); } }, pending.biteDelayMs + pending.windowMs + 80);
}

async function tryReel() {
  if (!pending || Date.now() < biteAt) return renderGame(t("tooSoon"));
  if (Date.now() > reelDeadline) { pending = null; return renderGame(t("tooLate")); }
  let result = pending;
  const reactionMs = Date.now() - biteAt;
  const session = gameSession;
  const activeRoom = room;
  const activeProfile = { ...profile };
  const activeAccessCode = accessCode;
  pending = null;
  if (result.token) {
    try { result = { ...result, token: (await reelCatch(activeRoom, activeProfile, result, reactionMs, activeAccessCode)).token }; }
    catch { if (session === gameSession) renderGame(t("tooLate")); return; }
  }
  if (session !== gameSession) return;
  landedTackle = tackle;
  landed = result;
  renderGame();
}

async function decideCatch(decision: CatchDecision) {
  if (!landed) return;
  const result = landed;
  const session = gameSession;
  const activeRoom = room;
  const activeProfile = { ...profile };
  const activeAccessCode = accessCode;
  landed = null;
  const month = astrakhanNow().getMonth();
  let outcome = evaluateCatchDecision(result, decision, keptKg, landedTackle, month);
  if (result.token) {
    try { outcome = await landCatch(activeRoom, activeProfile, result, decision, activeAccessCode); }
    catch { if (session === gameSession) renderGame(t("decisionRejected")); return; }
  }
  if (session !== gameSession) return;
  score += outcome.points;
  totalKg += result.weightKg;
  catches++;
  activity.catches++;
  keptKg = outcome.keptKg;
  if (decision === "release") activity.totalKg += result.weightKg;
  localStorage.setItem(progressKey("kept-kg"), String(keptKg));
  if (outcome.correct && decision === "keep" && !recipeUnlocked) {
    recipeUnlocked = true;
    localStorage.setItem(progressKey("recipe"), "yes");
  }
  if (outcome.correct && decision === "release" && result.species.protected) {
    guardianUnlocked = true;
    localStorage.setItem(progressKey("guardian"), "yes");
  }
  sendEvent("catch", { fishId: result.species.id, fish: `${result.weightKg} kg ${result.species.name}`, weightKg: result.weightKg, protected: Boolean(result.species.protected && outcome.correct && decision === "release"), decision });
  const feedback = (outcome.correct
    ? decision === "keep" ? t("correctKeep", { fish: fishName(locale, result.species.id, result.species.name), points: outcome.points }) : t("correctRelease", { reason: reasonText(outcome.reason), points: outcome.points })
    : t("wrongDecision", { reason: reasonText(outcome.reason), points: outcome.points })) + (outcome.correct && outcome.matchBonus ? t("matchBonus") : "");
  refreshStats(session, activeRoom).then((current) => { if (current) renderGame(feedback); });
}

renderLobby();
